import { COMMERCIAL_DOMAINS } from "./commercialProjectSchema";
import { listMatrixUserEmailsByRoleName } from "./commercialRoleAccess";
import { getViewerEmailFromStorage, sameOrgMailbox } from "./userMatrixStorage";

/** Distinct chip colors for inspector report events (inspector-only calendar view). */
export const INSPECTOR_REPORT_COLORS = [
  "#2563eb",
  "#7c3aed",
  "#059669",
  "#d97706",
  "#dc2626",
  "#0891b2",
  "#be185d",
  "#4f46e5",
  "#0d9488",
  "#ca8a04",
  "#9333ea",
  "#ea580c",
];

export function normalizeInspectorKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function inspectorLocalPart(value) {
  const key = normalizeInspectorKey(value);
  if (!key) return "";
  return key.split("@")[0] || key;
}

/** Known inspector emails from the User Matrix Inspector role. */
export function listKnownInspectorEmails() {
  return listMatrixUserEmailsByRoleName("Inspector");
}

/** Map legacy reported_by strings (names, short usernames) to a canonical inspector email key. */
export function canonicalInspectorKey(value, knownEmails = null) {
  const key = normalizeInspectorKey(value);
  if (!key || key === "unknown") return key;
  const known = knownEmails || listKnownInspectorEmails();

  for (const email of known) {
    const canon = normalizeInspectorKey(email);
    if (!canon) continue;
    if (key === canon || sameOrgMailbox(key, canon)) return canon;
    if (!key.includes("@") && inspectorLocalPart(canon) === key) return canon;
    if (formatInspectorLabel(key) === formatInspectorLabel(canon)) return canon;
  }

  if (key.includes("@")) {
    for (const email of known) {
      if (sameOrgMailbox(key, email)) return normalizeInspectorKey(email);
    }
  }

  return key;
}

export function formatInspectorLabel(email) {
  const normalized = normalizeInspectorKey(email);
  if (!normalized || normalized === "unknown") return "Unknown inspector";
  const local = normalized.split("@")[0] || normalized;
  const pretty = local.replace(/[._-]+/g, " ").trim();
  if (!pretty) return normalized;
  return pretty
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function inspectorInitials(email) {
  const label = formatInspectorLabel(email);
  if (label === "Unknown inspector") return "?";
  const parts = label.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return label.slice(0, 2).toUpperCase();
}

function isPlainObject(v) {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

export function readAssignedInspectorFromAcf(acfGroupsJson) {
  const j = isPlainObject(acfGroupsJson) ? acfGroupsJson : {};
  const cons =
    j[COMMERCIAL_DOMAINS.CONSTRUCTION] ??
    j.construction_details ??
    j["Construction Details"] ??
    {};
  return cons.inspector != null ? String(cons.inspector).trim() : "";
}

/** True when a daily progress row was filed by the viewer. */
export function dailyProgressReportEditableByViewer(row, viewerEmail) {
  const viewer = canonicalInspectorKey(viewerEmail || getViewerEmailFromStorage());
  if (!viewer) return false;
  const reporter = canonicalInspectorKey(row?.reported_by);
  if (!reporter) return false;
  return reporter === viewer;
}

export function resolveReportInspectorKey(report, row) {
  const reportedBy = canonicalInspectorKey(report?.reported_by);
  if (reportedBy) return reportedBy;
  const assigned = canonicalInspectorKey(readAssignedInspectorFromAcf(row?.acf_groups_json));
  if (assigned) return assigned;
  return "unknown";
}

export function isInspectorReportsOnlyFilter(filters) {
  return (
    filters?.inspectorReports === true &&
    filters?.crewReports === false &&
    filters?.construction === false &&
    filters?.splicing === false &&
    filters?.networking === false
  );
}

export function listTrackedInspectorEmails(extraEmails = []) {
  const known = listKnownInspectorEmails();
  const seen = new Set();
  const out = [];
  for (const email of [...known, ...extraEmails]) {
    const key = canonicalInspectorKey(email, known);
    if (!key || key === "unknown" || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

export function buildInspectorColorMap(inspectorEmails) {
  const map = new Map();
  inspectorEmails.forEach((email, index) => {
    map.set(normalizeInspectorKey(email), INSPECTOR_REPORT_COLORS[index % INSPECTOR_REPORT_COLORS.length]);
  });
  return map;
}

export function applyInspectorColorsToEvents(events, colorMap) {
  return events.map((event) => {
    if (event.eventType !== "inspectorReports") return event;
    const inspectorKey = normalizeInspectorKey(event.inspectorKey);
    const color = colorMap.get(inspectorKey) || INSPECTOR_REPORT_COLORS[0];
    return {
      ...event,
      color,
      typeShort: inspectorInitials(inspectorKey),
      inspectorLabel: formatInspectorLabel(inspectorKey),
    };
  });
}

export function isWeekday(date) {
  const day = date.getDay();
  return day >= 1 && day <= 5;
}

export function monthInputValue(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

export function parseMonthInputValue(value) {
  if (value == null || String(value).trim() === "") return null;
  const m = String(value).trim().match(/^(\d{4})-(\d{2})$/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  return Number.isNaN(d.getTime()) ? null : d;
}
